/**
 * The "time's up" sounds for timed steps (2026-10-19, Jens: a choice of 3–5 sounds). Made in the
 * browser (Web Audio), so there are no sound files to download. Each sound is a list of notes;
 * playTimerSound turns them into tones.
 *
 * The choice is per person on each device (a family tablet has several people), kept with the
 * app's other local settings (antnest: keys, which the iOS/Android apps also back up).
 */
export const TIMER_SOUNDS = ['bell', 'chime', 'beeps', 'marimba', 'fanfare'] as const;
export type TimerSound = (typeof TIMER_SOUNDS)[number];
export const DEFAULT_TIMER_SOUND: TimerSound = 'bell';

export type Note = { freq: number; start: number; dur: number; wave: OscillatorType; gain: number };

const repeat = (notes: Note[], times: number, every: number): Note[] =>
  Array.from({ length: times }, (_, i) => notes.map((n) => ({ ...n, start: n.start + i * every }))).flat();

/** The notes of a sound: frequency (Hz), start and length (seconds), wave shape and loudness. */
export function soundNotes(sound: TimerSound): Note[] {
  switch (sound) {
    case 'bell': {
      // A struck bell: a few partials that fade slowly; rung three times.
      const strike: Note[] = [
        { freq: 880, start: 0, dur: 1.4, wave: 'sine', gain: 0.5 },
        { freq: 2429, start: 0, dur: 0.8, wave: 'sine', gain: 0.18 },
        { freq: 4752, start: 0, dur: 0.4, wave: 'sine', gain: 0.08 },
      ];
      return repeat(strike, 3, 1.1);
    }
    case 'chime':
      // Three rising notes (C – E – G), twice.
      return repeat([1047, 1319, 1568].map((freq, i) => ({ freq, start: i * 0.28, dur: 0.9, wave: 'triangle' as OscillatorType, gain: 0.45 })), 2, 1.5);
    case 'beeps':
      // The classic alarm clock: four quick beeps, three times.
      return repeat([0, 1, 2, 3].map((i) => ({ freq: 1000, start: i * 0.2, dur: 0.12, wave: 'square' as OscillatorType, gain: 0.18 })), 3, 1.1);
    case 'marimba':
      // Soft wooden notes going up (G – B – D – G), twice.
      return repeat([784, 988, 1175, 1568].map((freq, i) => ({ freq, start: i * 0.16, dur: 0.45, wave: 'sine' as OscillatorType, gain: 0.55 })), 2, 1.3);
    case 'fanfare':
      // Ta-ta-ta-taaa!
      return [
        { freq: 523, start: 0, dur: 0.16, wave: 'triangle', gain: 0.45 },
        { freq: 523, start: 0.2, dur: 0.16, wave: 'triangle', gain: 0.45 },
        { freq: 523, start: 0.4, dur: 0.16, wave: 'triangle', gain: 0.45 },
        { freq: 784, start: 0.6, dur: 1.2, wave: 'triangle', gain: 0.5 },
        { freq: 659, start: 0.6, dur: 1.2, wave: 'triangle', gain: 0.25 },
      ];
  }
}

/** How long a sound lasts, in seconds. */
export const soundLength = (sound: TimerSound): number =>
  Math.max(...soundNotes(sound).map((n) => n.start + n.dur));

export const isTimerSound = (v: unknown): v is TimerSound => TIMER_SOUNDS.includes(v as TimerSound);

const storageKey = (personId: string | null | undefined) => `antnest:timer_sound:${personId || 'device'}`;

export function getTimerSound(personId: string | null | undefined, storage: Pick<Storage, 'getItem'> | null = safeStorage()): TimerSound {
  try {
    const v = storage?.getItem(storageKey(personId));
    return isTimerSound(v) ? v : DEFAULT_TIMER_SOUND;
  } catch {
    return DEFAULT_TIMER_SOUND;
  }
}

export function setTimerSound(personId: string | null | undefined, sound: TimerSound, storage: Pick<Storage, 'setItem'> | null = safeStorage()): void {
  try { storage?.setItem(storageKey(personId), sound); } catch { /* private mode: just not remembered */ }
}

function safeStorage(): Storage | null {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch { return null; }
}

// ── Playing ─────────────────────────────────────────────────────────────────────────────────────
let ctx: AudioContext | null = null;

/**
 * Call from a tap (Start timer / choosing a sound): browsers, iPhones especially, only allow sound
 * after the person has touched the page. Also asks iOS to treat it as media, so it can play when
 * the phone is set to silent (Safari 17+; older versions just ignore it).
 */
export function unlockTimerAudio(): void {
  try {
    const session = (navigator as any).audioSession;
    if (session && session.type !== 'playback') session.type = 'playback';
  } catch { /* not supported */ }
  try {
    const AC: typeof AudioContext | undefined = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    if (!ctx) ctx = new AC();
    if (ctx.state === 'suspended') void ctx.resume();
    // A silent blip finishes the unlock on older iPhones.
    const buffer = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    src.start(0);
  } catch { /* no sound on this device */ }
}

export function playTimerSound(sound: TimerSound): void {
  try {
    if (!ctx) unlockTimerAudio();
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume();
    const t0 = ctx.currentTime + 0.05;
    for (const n of soundNotes(sound)) {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = n.wave;
      osc.frequency.value = n.freq;
      // Quick attack, then fade out (no clicks).
      g.gain.setValueAtTime(0.0001, t0 + n.start);
      g.gain.exponentialRampToValueAtTime(n.gain, t0 + n.start + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + n.start + n.dur);
      osc.connect(g).connect(ctx.destination);
      osc.start(t0 + n.start);
      osc.stop(t0 + n.start + n.dur + 0.05);
    }
  } catch { /* no sound on this device */ }
}
